import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, RefreshCw, Search, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getCommunities } from "../api/communityApi";
import { CommunityCard, CommunityLogo } from "../components/Community/CommunityUI";
import { COMMUNITY_TYPES, getCommunityTypeLabel } from "../components/Community/communityUtils";
import useCommunityMembership from "../components/Community/useCommunityMembership";
import { SkeletonBlock } from "../components/Teams/TeamUI";

const TYPE_FILTERS = ["all", ...Object.keys(COMMUNITY_TYPES)];
const SEARCH_DELAY_MS = 300;

const Communities = () => {
  const { t } = useTranslation();
  const { community: myCommunity } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [type, setType] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getCommunities({ search, type: type === "all" ? undefined : type });
      setCommunities(response.communities || []);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [search, type]);

  useEffect(() => {
    load();
  }, [load]);

  const { requestJoin, modal } = useCommunityMembership({ onChange: load });

  // Your own community is pinned in the hero, so leave it out of the grid.
  const others = useMemo(
    () => communities.filter((community) => community.id !== myCommunity?.id),
    [communities, myCommunity?.id]
  );

  return (
    <div className="mx-auto max-w-6xl pb-16">
      {/* Hero */}
      <section className="overflow-hidden rounded-3xl bg-[#120239] text-white shadow-xl">
        <div className="relative px-6 py-8 md:px-10">
          <div
            className="pointer-events-none absolute -end-16 -top-16 h-56 w-56 rounded-full bg-[#FF9200]/30 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-200">
                <Users size={14} aria-hidden="true" />
                {t("communitiesEyebrow", { defaultValue: "Communities" })}
              </p>
              <h1 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl">
                {t("joinCommunity", { defaultValue: "Join Community" })}
              </h1>
              <p className="mt-2 max-w-xl text-sm text-slate-300 md:text-base">
                {t("communitiesIntro", {
                  defaultValue:
                    "Represent your school, club or company. Pick one community and its logo shows on your profile.",
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={isLoading}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 self-start rounded-full bg-white px-5 text-sm font-semibold text-[#120239] transition hover:bg-orange-50 disabled:opacity-60 md:self-auto"
            >
              <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} aria-hidden="true" />
              {t("refresh", { defaultValue: "Refresh" })}
            </button>
          </div>

          {myCommunity && (
            <Link
              to={`/communities/${myCommunity.id}`}
              className="relative mt-6 flex items-center gap-4 rounded-2xl bg-white/10 p-4 ring-1 ring-orange-400/40 transition hover:bg-white/15"
            >
              <CommunityLogo community={myCommunity} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-200">
                  {t("yourCommunity", { defaultValue: "Your community" })}
                </p>
                <p className="truncate text-lg font-bold">{myCommunity.name}</p>
              </div>
              <ChevronRight size={20} className="shrink-0 rtl:rotate-180" aria-hidden="true" />
            </Link>
          )}
        </div>
      </section>

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3">
        <label className="relative block">
          <span className="sr-only">{t("searchCommunities", { defaultValue: "Search communities" })}</span>
          <Search size={18} className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t("searchCommunitiesPlaceholder", { defaultValue: "Search by name, tagline or address" })}
            className="min-h-[44px] w-full rounded-full border border-gray-300 bg-white pe-4 ps-11 text-sm"
          />
        </label>
        <div
          className="flex gap-2 overflow-x-auto pb-1"
          role="group"
          aria-label={t("filterByCommunityType", { defaultValue: "Filter by community type" })}
        >
          {TYPE_FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={type === value}
              onClick={() => setType(value)}
              className={`min-h-[44px] shrink-0 rounded-full px-4 text-sm font-semibold transition ${
                type === value
                  ? "bg-[#120239] text-white shadow"
                  : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
              }`}
            >
              {value === "all"
                ? t("allCommunities", { defaultValue: "All" })
                : getCommunityTypeLabel(value, t)}
            </button>
          ))}
        </div>
      </div>

      {loadError && (
        <div role="alert" className="mt-6 flex flex-col items-start gap-3 rounded-3xl bg-rose-50 p-5 text-rose-800 ring-1 ring-rose-200 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="min-h-[44px] rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
          >
            {t("tryAgain", { defaultValue: "Try again" })}
          </button>
        </div>
      )}

      <div className="mt-6">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((key) => (
              <SkeletonBlock key={key} className="h-52" />
            ))}
          </div>
        ) : others.length === 0 && !loadError ? (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center">
            <Users size={32} className="mx-auto text-orange-400" aria-hidden="true" />
            <p className="mt-3 font-semibold text-gray-900">
              {search || type !== "all"
                ? t("noCommunitiesMatch", { defaultValue: "No communities match your search" })
                : t("noCommunitiesTitle", { defaultValue: "No communities yet" })}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {t("noCommunitiesBody", {
                defaultValue: "Want your school, club or company here? Contact VIGOR support.",
              })}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((community) => (
              <CommunityCard key={community.id} community={community} onJoin={requestJoin} />
            ))}
          </div>
        )}
      </div>

      {modal}
    </div>
  );
};

export default Communities;
